import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import { select, selectAll } from "../../utils";
import { state } from "../state";

export function initProductPage(): void {
  const overlay = select(".overlay");
  const elImage = select(".element__image__wrapper img");
  const closeOverlay = select(".overlay__close");
  const count = select(".count");
  const countOf = select(".of");
  const bottomLeft = select(".bottom__left");
  const imgWrapper = select(".overlay__img__wrapper");
  const contentImages = selectAll(
    ".element__content__image img",
  ) as any as HTMLImageElement[];

  if (!imgWrapper) return;

  // --- Build slides from element__content__inner sections ---
  const details = gsap.utils.toArray(
    ".element__content__inner",
  ) as HTMLElement[];
  const slides: { img: HTMLImageElement; caption: string }[] = [];

  details.forEach((detail) => {
    const detailImgs = detail.querySelectorAll(".element__content__image img");
    const detailTexts = detail.querySelectorAll(
      ".element__content__text p:not(.english)",
    );
    const captionHtml = detailTexts.length
      ? Array.from(detailTexts)
          .map((p) => (p as HTMLElement).outerHTML)
          .join("")
      : "";

    detailImgs.forEach((srcImg) => {
      const newImg = document.createElement("img");
      newImg.className = "image";
      newImg.src = srcImg.getAttribute("src") || "";
      newImg.alt = srcImg.getAttribute("alt") || "";
      imgWrapper.appendChild(newImg);
      slides.push({ img: newImg, caption: captionHtml });
    });
  });

  if (!slides.length) return;

  // Reverse to match original stacking order (last on top)
  slides.reverse();

  const images = slides.map((s) => s.img);
  const captions = slides.map((s) => s.caption);

  const wrap = gsap.utils.wrap(0, images.length);

  if (count) count.textContent = String(1);
  if (countOf) countOf.textContent = `0${images.length}`;

  const updateCaption = (index: number) => {
    if (!bottomLeft) return;
    bottomLeft.innerHTML = captions[index] || "";
  };

  let animating = false;
  let currentIndex = 0;

  function gotoSection(index: number, direction: number) {
    if (!count) return;
    animating = true;
    index = wrap(index);
    const tl = gsap.timeline({
      defaults: { duration: 1, ease: "expo.inOut" },
      onComplete: () => {
        animating = false;
      },
    });
    tl.set(count, { textContent: String(`0${index + 1}`) }, 0.32)
      .add(() => updateCaption(index), 0.32)
      .fromTo(
        images[index],
        { xPercent: 125 * direction, scaleX: 1.5, scaleY: 1.3 },
        { xPercent: 0, scaleX: 1, scaleY: 1, duration: 1 },
        0,
      )
      .fromTo(
        images[currentIndex],
        { xPercent: 0, scaleX: 1, scaleY: 1 },
        { xPercent: -125 * direction, scaleX: 1.5, scaleY: 1.3 },
        0,
      )
      .timeScale(0.8);
    currentIndex = index;
  }

  let observerInstance: any = null;
  Observer.create({
    id: "page-overlay",
    type: "wheel,touch,pointer",
    preventDefault: true,
    wheelSpeed: -1,
    onUp: () => {
      if (!animating) gotoSection(currentIndex + 1, +1);
    },
    onDown: () => {
      if (!animating) gotoSection(currentIndex - 1, -1);
    },
    tolerance: 10,
  });
  observerInstance = Observer.getById("page-overlay");
  if (observerInstance) observerInstance.disable();

  const onKeydown = (e: KeyboardEvent) => {
    if (animating) return;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft")
      gotoSection(currentIndex - 1, -1);
    if (e.key === "ArrowDown" || e.key === "ArrowRight" || e.key === "Enter")
      gotoSection(currentIndex + 1, 1);
  };

  // --- Function to open/close the gallery ---
  const openGallery = () => {
    if (!elImage || !overlay) return;
    elImage.classList.add("active");
    overlay.classList.add("active");
    updateCaption(currentIndex);
    observerInstance?.enable();
    document.addEventListener("keydown", onKeydown);
  };

  const closeGallery = () => {
    if (!elImage || !overlay) return;
    elImage.classList.remove("active");
    overlay.classList.remove("active");
    observerInstance?.disable();
    document.removeEventListener("keydown", onKeydown);
  };

  if (elImage && overlay) {
    elImage.addEventListener("click", () => {
      if (overlay.classList.contains("active")) {
        closeGallery();
      } else {
        openGallery();
      }
    });
  }

  if (closeOverlay) {
    closeOverlay.addEventListener("click", () => {
      closeGallery();
    });
  }

  // --- Auto-open gallery on product page arrival ---
  state.autoOpenGallery = false;
  openGallery();

  if (contentImages?.length) {
    contentImages.forEach((img) => {
      gsap.set(img, { opacity: 0, y: 30, scale: 0.95 });
      const tl = gsap.timeline({
        paused: true,
        smoothChildTiming: true,
        onComplete: () => {
          gsap.set(img, { clearProps: "opacity,transform" });
        },
        onReverseComplete: () => {
          gsap.set(img, { opacity: 0, y: 30, scale: 0.95 });
        },
      });
      tl.to(img, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.2,
        ease: "power2.out",
      });
      ScrollTrigger.create({
        animation: tl,
        trigger: img,
        start: "top 75%",
        end: "top 25%",
        scrub: true,
        fastScrollEnd: true,
      });
    });
  }
}
